import { useState, useCallback } from 'react';

export type CopyAlertState = {
  isPresented: boolean;
  messageKey: 'copied_to_clipboard' | 'try_again';
  severity: 'success' | 'error';
};

type ClipboardAPI = typeof navigator.clipboard;

type UseCopyEmailReturn = {
  copyAlertState: CopyAlertState;
  handleCopyEmail: (email: string) => void;
  dismissCopyAlert: () => void;
};

export const useCopyEmail = (
  clipboardAPI: ClipboardAPI = navigator.clipboard,
): UseCopyEmailReturn => {
  const [copyAlertState, setCopyAlertState] = useState<CopyAlertState>({
    isPresented: false,
    messageKey: 'copied_to_clipboard',
    severity: 'success',
  });

  const handleCopyEmail = useCallback(
    (email: string) => {
      if (!email) {
        return;
      }

      clipboardAPI
        .writeText(email)
        .then(() => {
          setCopyAlertState({
            isPresented: true,
            messageKey: 'copied_to_clipboard',
            severity: 'success',
          });
        })
        .catch(() => {
          setCopyAlertState({
            isPresented: true,
            messageKey: 'try_again',
            severity: 'error',
          });
        });
    },
    [clipboardAPI],
  );

  const dismissCopyAlert = useCallback(() => {
    setCopyAlertState((prevState) => ({
      ...prevState,
      isPresented: false,
    }));
  }, []);

  return {
    copyAlertState,
    handleCopyEmail,
    dismissCopyAlert,
  };
};
